import { useRef, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Container } from '@/components/layout/Container';
import { Section } from '@/components/layout/Section';
import { Eyebrow } from '@/components/domain/Eyebrow';
import { Button } from '@/components/primitives/Button';
import { useFadeUpOnScroll } from '@/hooks/useFadeUpOnScroll';
import styles from './TrustVerificationSection.module.css';

export const TrustVerificationSection: FC = () => {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  useFadeUpOnScroll(ref, { selector: '[data-fade]', stagger: 0.08 });

  const checks = t('home.trustVerify.checks', { returnObjects: true }) as Array<{
    question: string;
    answer: string;
  }>;

  return (
    <Section tone="dark" id="trust-verify">
      <Container size="default">
        <div ref={ref}>
          <header className={styles.header}>
            <div data-fade>
              <Eyebrow tone="inverse">{t('home.trustVerify.kicker')}</Eyebrow>
            </div>
            <h2 className={styles.title} data-fade>
              {t('home.trustVerify.title')}
            </h2>
          </header>

          <div className={styles.card} data-fade>
            <p className={styles.registryTitle}>{t('home.trustVerify.registryTitle')}</p>

            <div className={styles.inputRow}>
              <input
                type="text"
                className={styles.input}
                placeholder={t('home.trustVerify.placeholder')}
                aria-label={t('home.trustVerify.placeholder')}
              />
              <Button variant="primary" size="md">
                {t('home.trustVerify.verify')}
              </Button>
            </div>

            <ul className={styles.checks}>
              {checks.map((c) => (
                <li key={c.question} className={styles.check}>
                  <span className={styles.question}>{c.question}</span>
                  <span className={styles.answer}>{c.answer}</span>
                </li>
              ))}
            </ul>
          </div>

          <p className={styles.quote} data-fade>
            {t('home.trustVerify.quote')}
          </p>
        </div>
      </Container>
    </Section>
  );
};

TrustVerificationSection.displayName = 'TrustVerificationSection';
